import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { from } from 'rxjs';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { AuthService } from '@/core/auth/auth.service';
import { OficioDeferimentoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/oficio-deferimento-pj/oficio-deferimento-pj.service';
import { ReportOficioDeferimentoPjRequest, ReportOficioDeferimentoPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/oficio-deferimento-pj/oficio-deferimento-pj.model';
import { OficioDeferimentoPjJobService } from '@/core/services/oficio-deferimento-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportOficioDeferimentoPjRequest;
type Resp = ReportOficioDeferimentoPjResponse;

@Component({
    selector: 'app-oficio-deferimento-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './oficio-deferimento-pj.html',
    styleUrl: './oficio-deferimento-pj.scss'
})
/**
 * Relatório PDF assíncrono: Ofício de Deferimento (prestador PJ).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`) e o `emissor` (nome do usuário logado).
 */
export class ReportOficioDeferimentoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly relatorioService = inject(OficioDeferimentoPjService);
    private readonly authService = inject(AuthService);
    private readonly pessoaService = inject(PessoaService);
    readonly jobService = inject(OficioDeferimentoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    /** Nome do usuário logado, impresso como emissor no rodapé do ofício. */
    private usuarioNome = '';

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Restaura o job da sessão (base) e resolve o nome do usuário logado. */
    override ngOnInit(): void {
        super.ngOnInit();
        this.inicializarUsuarioLogado();
    }

    private inicializarUsuarioLogado(): void {
        from(this.authService.getUserInfo()).subscribe((userInfo) => {
            this.usuarioNome = userInfo.name || '';
        });
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o ofício.'
            });
            return null;
        }

        return {
            nuCrm,
            sgUf,
            emissor: this.usuarioNome || (this.authService.identityClaims?.['preferred_username'] as string) || 'Sistema'
        };
    }

    protected exportar(request: Req) {
        return this.relatorioService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
